import { HTMLAttributes, ReactNode } from 'react';
import './Badge.css';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /**
   * Visual variant of the badge
   * @default 'default'
   */
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info';

  /**
   * Size of the badge
   * @default 'base'
   */
  size?: 'sm' | 'base' | 'lg';

  /**
   * Show as a dot badge (small colored circle)
   * @default false
   */
  dot?: boolean;

  /**
   * Make badge removable with close button
   * @default false
   */
  removable?: boolean;

  /**
   * Callback when remove button is clicked
   */
  onRemove?: () => void;

  /**
   * Badge content
   */
  children?: ReactNode;
}

/**
 * Badge component for status indicators, labels, and counts
 *
 * @example
 * ```tsx
 * <Badge variant="success">Active</Badge>
 * <Badge variant="error" size="sm">3</Badge>
 * <Badge dot variant="warning" />
 * <Badge removable onRemove={() => console.log('removed')}>
 *   Tag
 * </Badge>
 * ```
 */
export const Badge: React.FC<BadgeProps> = ({
  variant = 'default',
  size = 'base',
  dot = false,
  removable = false,
  onRemove,
  children,
  className = '',
  ...props
}) => {
  const classNames = [
    'badge',
    `badge-${variant}`,
    `badge-${size}`,
    dot && 'badge-dot',
    removable && 'badge-removable',
    className
  ].filter(Boolean).join(' ');

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemove) {
      onRemove();
    }
  };

  if (dot) {
    return (
      <span className={classNames} {...props} />
    );
  }

  return (
    <span className={classNames} {...props}>
      <span className="badge-content">{children}</span>
      {removable && (
        <button
          type="button"
          className="badge-remove"
          onClick={handleRemove}
          aria-label="Remove"
        >
          ✕
        </button>
      )}
    </span>
  );
};

export default Badge;
